// SCR-010: one customer-month's usage adjustments (/customers/:orgId/invoices/:period/adjustments).
// A row opens its summary with Approve and Void (Void asks first). Approve is offered to any `billing:approve` holder, including the row's own creator (never
// hidden) — see model.ts's `canApproveAdjustment`. `?new=1` (the Billing tab's "Add usage
// adjustment") opens the New adjustment dialog for a `billing:write` session.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext } from "../../session/session";
import { useCustomerName } from "../../ui/customer-directory";
import { ConfirmDialog, Dialog } from "@nexus/ui";
import { errorId, Field, hintId, InputAffix } from "@nexus/ui";
import { hasErrors } from "@nexus/ui";
import { actorTitle, knownActor } from "../../ui/identity";
import { Icon } from "@nexus/ui";
import { isMonth, longMonthLabel } from "@nexus/ui";
import { Chip, NameCell, PageHeader } from "@nexus/ui";
import { Link, useBreadcrumb } from "../../ui/shell";
import { ErrorBanner } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { DataTable, type Column } from "@nexus/ui";
import { customerPath } from "../orgs/model";
import {
  ADJUSTMENT_KIND_LABELS,
  ADJUSTMENT_KIND_OPTIONS,
  ADJUSTMENT_STATUS_TONES,
  TOKEN_CLASS_LABELS,
  TOKEN_CLASS_OPTIONS,
  adjustmentFieldsFor,
  adjustmentRow,
  buildCreateAdjustmentRequest,
  emptyAdjustmentForm,
  invoicePath,
  loadAdjustments,
  validateAdjustmentForm,
  type AdjustmentFormValues,
  type AdjustmentRow,
  type UsageAdjustment,
} from "./model";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

function Amount({ display }: { display: AdjustmentRow["amountDisplay"] }) {
  if (!display) return <>—</>;
  return (
    <span class={display.negative ? "money money-negative" : "money"} title={display.exact}>
      {display.text}
    </span>
  );
}

interface Row {
  adjustment: UsageAdjustment;
  view: AdjustmentRow;
}

export function Adjustments({ params, query }: { params: Record<string, string>; query: URLSearchParams }) {
  const orgId = params.orgId ?? "";
  const period = params.period ?? "";
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canApprovePermission = session?.can("billing:approve") ?? false;
  const canWritePermission = session?.can("billing:write") ?? false;
  const name = useCustomerName(orgId);
  const monthText = isMonth(period) ? longMonthLabel(period) : period;

  const [adjustments, setAdjustments] = useState<readonly UsageAdjustment[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [dialogOpen, setDialogOpen] = useState(() => canWritePermission && query.get("new") === "1");
  const [form, setForm] = useState<AdjustmentFormValues>(emptyAdjustmentForm());
  const [createError, setCreateError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [voiding, setVoiding] = useState<Row | null>(null);

  useBreadcrumb([
    { label: name, href: customerPath(orgId, "billing") },
    { label: monthText, href: invoicePath(orgId, period) },
    { label: "Usage adjustments" },
  ]);

  const reload = () => {
    if (!orgId) return;
    setError(null);
    void loadAdjustments(client, orgId, period || undefined).then((r) => ("items" in r ? setAdjustments(r.items) : setError(r.error)));
  };

  useEffect(reload, [client, orgId, period]);

  const act = async (fn: () => Promise<UsageAdjustment>) => {
    setActionError(null);
    try {
      const updated = await fn();
      setAdjustments((current) => (current ? current.map((a) => (a.id === updated.id ? updated : a)) : current));
    } catch (e) {
      setActionError(unexpected(e));
    }
  };

  // The summary closes while the confirmation is up and reopens on the outcome, a refusal included.
  const confirmVoid = async () => {
    if (!voiding) return;
    const adjustmentId = voiding.adjustment.id;
    setBusy(true);
    await act(() => client.post("/v1/admin/orgs/{orgId}/usage-adjustments/{adjustmentId}/void", { path: { orgId, adjustmentId } }));
    setBusy(false);
    setVoiding(null);
    setSelectedId(adjustmentId);
  };

  const openRow = (r: Row) => {
    setActionError(null);
    setSelectedId(r.adjustment.id);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCreateError(null);
  };

  const create = async () => {
    setBusy(true);
    setCreateError(null);
    try {
      const created = await client.post("/v1/admin/orgs/{orgId}/usage-adjustments", {
        path: { orgId },
        body: buildCreateAdjustmentRequest(form, period || undefined),
      });
      setAdjustments((current) => (current ? [created, ...current] : [created]));
      setForm(emptyAdjustmentForm());
      setDialogOpen(false);
    } catch (e) {
      setCreateError(unexpected(e));
    } finally {
      setBusy(false);
    }
  };

  const need = adjustmentFieldsFor(form.kind);
  const formErrors = validateAdjustmentForm(form);
  // Say what is wrong only once the operator has typed into the field.
  const shown = (field: keyof AdjustmentFormValues) => (form[field] === "" ? undefined : formErrors[field]);

  const rows: Row[] = (adjustments ?? []).map((adjustment) => ({
    adjustment,
    view: adjustmentRow(adjustment, canApprovePermission, canWritePermission, session?.me),
  }));
  const selected = rows.find((r) => r.adjustment.id === selectedId) ?? null;

  const columns: Column<Row>[] = [
    { key: "kind", header: "Kind", help: "terms.adjustmentKind", cell: (r) => r.view.kindLabel },
    { key: "model", header: "Model / class", cell: (r) => <span class="mono">{r.view.modelClass}</span> },
    { key: "tokens", header: "Tokens", numeric: true, cell: (r) => r.view.tokensLabel ?? "—" },
    {
      key: "amount",
      header: "Amount",
      numeric: true,
      cell: (r) => <Amount display={r.view.amountDisplay} />,
    },
    { key: "reason", header: "Reason", cell: (r) => r.view.reason },
    {
      key: "status",
      header: "Status",
      cell: (r) => <Chip tone={ADJUSTMENT_STATUS_TONES[r.adjustment.status]}>{r.view.statusLabel}</Chip>,
    },
    { key: "createdBy", header: "Created by", cell: (r) => <span title={actorTitle(r.adjustment.createdBy, knownActor(r.adjustment, "createdBy"))}>{r.view.createdByLabel}</span> },
    { key: "approvedBy", header: "Approved by", cell: (r) => <span title={actorTitle(r.adjustment.approvedBy, knownActor(r.adjustment, "approvedBy"))}>{r.view.approvedByLabel}</span> },
  ];

  const newButton = canWritePermission && (
    <button type="button" class="button button-primary" onClick={() => setDialogOpen(true)}>
      <Icon name="plus" />
      New adjustment
    </button>
  );

  return (
    <div class="page">
      <PageHeader
        title="Usage adjustments"
        lede={`Credits and corrections to ${name}'s ${monthText} invoice. Only approved adjustments reach the invoice, when the month closes.`}
        meta={
          <>
            <NameCell name={name} id={orgId} href={customerPath(orgId, "billing")} />
            <Link href={invoicePath(orgId, period)}>{monthText} invoice</Link>
          </>
        }
        actions={newButton}
      />

      <div class="table-card">
        <DataTable
          caption="Usage adjustments"
          hideCaption
          columns={columns}
          rows={rows}
          rowKey={(r) => r.adjustment.id}
          loading={adjustments === null && !error}
          error={error}
          onRetry={reload}
          emptyMessage={`No adjustments for ${monthText}.`}
          emptyAction={newButton || undefined}
          onRowClick={openRow}
          rowLabel={(r) => `${r.view.kindLabel}: ${r.view.reason}`}
        />
      </div>

      {selected && (
        <RowDetail
          title={`${selected.view.kindLabel} · ${monthText}`}
          open
          onClose={() => setSelectedId(null)}
          facts={[
            { label: "Status", value: <Chip tone={ADJUSTMENT_STATUS_TONES[selected.adjustment.status]}>{selected.view.statusLabel}</Chip> },
            { label: "Model / class", value: <span class="mono">{selected.view.modelClass}</span> },
            { label: "Tokens", value: selected.view.tokensLabel ?? "—" },
            { label: "Amount", value: <Amount display={selected.view.amountDisplay} /> },
            { label: "Reason", value: selected.view.reason },
            { label: "Created by", value: selected.view.createdByLabel },
            { label: "Approved by", value: selected.view.approvedByLabel },
          ]}
          actions={
            <>
              {selected.view.canVoid && (
                <button type="button" class="button button-secondary" onClick={() => (setSelectedId(null), setVoiding(selected))}>
                  Void
                </button>
              )}
              {selected.view.canApprove && (
                <button
                  type="button"
                  class="button button-primary"
                  onClick={() =>
                    void act(() =>
                      client.post("/v1/admin/orgs/{orgId}/usage-adjustments/{adjustmentId}/approve", { path: { orgId, adjustmentId: selected.adjustment.id } }),
                    )
                  }
                >
                  Approve
                </button>
              )}
            </>
          }
        >
          {actionError && <ErrorBanner error={actionError} />}
        </RowDetail>
      )}

      <ConfirmDialog
        title="Void adjustment"
        open={voiding !== null}
        message={voiding ? `Voiding this ${voiding.view.kindLabel.toLowerCase()} (${voiding.view.reason}) takes it off ${monthText}'s invoice for good.` : ""}
        confirmLabel="Void"
        busy={busy}
        onConfirm={() => void confirmVoid()}
        onClose={() => setVoiding(null)}
      />

      <Dialog
        title="New adjustment"
        open={dialogOpen}
        onClose={closeDialog}
        actions={
          <>
            <button type="button" class="button button-secondary" onClick={closeDialog}>
              Cancel
            </button>
            <button type="button" class="button button-primary" disabled={busy || hasErrors(formErrors)} onClick={() => void create()}>
              {busy ? "Creating…" : "Create"}
            </button>
          </>
        }
      >
        <div class="stack">
          <Field id="adjustment-kind" label="Kind" help="terms.adjustmentKind">
            <select id="adjustment-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.currentTarget.value as AdjustmentFormValues["kind"] })}>
              {ADJUSTMENT_KIND_OPTIONS.map((kind) => (
                <option key={kind} value={kind}>
                  {ADJUSTMENT_KIND_LABELS[kind]}
                </option>
              ))}
            </select>
          </Field>
          {need.amount && (
            <Field id="adjustment-amount" label="Amount" help="terms.adjustmentAmount" error={shown("amountUsd")}>
              <InputAffix prefix="$">
                <input
                  id="adjustment-amount"
                  type="text"
                  inputMode="decimal"
                  value={form.amountUsd}
                  aria-invalid={shown("amountUsd") ? true : undefined}
                  aria-describedby={shown("amountUsd") ? errorId("adjustment-amount") : undefined}
                  onInput={(e) => setForm({ ...form, amountUsd: e.currentTarget.value })}
                />
              </InputAffix>
            </Field>
          )}
          {need.model && (
            <>
              <Field id="adjustment-model" label="Model" help="common.model" error={shown("bedrockModelId")}>
                <input
                  id="adjustment-model"
                  type="text"
                  class="input-mono"
                  placeholder="anthropic.claude-sonnet-4-5"
                  value={form.bedrockModelId}
                  aria-invalid={shown("bedrockModelId") ? true : undefined}
                  aria-describedby={shown("bedrockModelId") ? errorId("adjustment-model") : undefined}
                  onInput={(e) => setForm({ ...form, bedrockModelId: e.currentTarget.value })}
                />
              </Field>
              <Field id="adjustment-class" label="Token class" help="terms.adjustmentTokenClass">
                <select
                  id="adjustment-class"
                  value={form.tokenClass}
                  onChange={(e) => setForm({ ...form, tokenClass: e.currentTarget.value as AdjustmentFormValues["tokenClass"] })}
                >
                  <option value="">Choose…</option>
                  {TOKEN_CLASS_OPTIONS.map((tc) => (
                    <option key={tc} value={tc}>
                      {TOKEN_CLASS_LABELS[tc]}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          {need.tokens && (
            <Field id="adjustment-tokens" label="Tokens" help="terms.adjustmentTokens" error={shown("tokens")} hint="Negative to remove tokens (a refund).">
              <input
                id="adjustment-tokens"
                type="text"
                inputMode="numeric"
                value={form.tokens}
                aria-invalid={shown("tokens") ? true : undefined}
                aria-describedby={shown("tokens") ? errorId("adjustment-tokens") : hintId("adjustment-tokens")}
                onInput={(e) => setForm({ ...form, tokens: e.currentTarget.value })}
              />
            </Field>
          )}
          <Field id="adjustment-reason" label="Reason" help="terms.adjustmentReason" error={shown("reason")} hint="10 to 500 characters; kept in the audit log.">
            <textarea
              id="adjustment-reason"
              value={form.reason}
              aria-invalid={shown("reason") ? true : undefined}
              aria-describedby={shown("reason") ? errorId("adjustment-reason") : hintId("adjustment-reason")}
              onInput={(e) => setForm({ ...form, reason: e.currentTarget.value })}
            />
          </Field>
          {createError && (
            <div class="banner banner-error" role="alert">
              <p>
                <code class="error-code">{createError.code}</code> {createError.message}
              </p>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
